import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CustomerLoyaltyData } from '../../../modals/product.modals';
import { ClientSettingsService } from '../../../services/client-settings.service';
import { ProductDbService } from '../../../services/productdb.service';
import { getLoyaltyExpiryClass, getLoyaltyExpiryDays, getLoyaltyExpiryText } from '../utils/loyalty.util';

/**
 * LoyaltyService
 *
 * Owns the loyalty-points domain for the Payment Total Dialog: state
 * (points/amount available, points entered, expiry membership data) plus
 * every calculation/validation rule around redeeming them.
 *
 * Provided at PaymentTotalDialogComponent level (see that component's
 * `providers: [LoyaltyService]`) — scoped to the dialog's lifecycle,
 * matching today's behavior where DashboardComponent.closeTotalDialog()/
 * resetLoyaltyData() explicitly reset this state on dialog close.
 *
 * orderTotal / isExchangeOrder are passed in by the caller rather than
 * owned here — they're cart-wide concerns (shared with the tax/discount
 * calculations in PosCalculationService), not loyalty-specific.
 */
@Injectable()
export class LoyaltyService {
  customerLoyaltyData: CustomerLoyaltyData | null = null;
  loyaltyPoints: number = 0; // Total available loyalty points
  loyaltyAmount: number = 0; // Total available loyalty amount (points * redeem_amount)
  loyaltyPointsInput: number = 0; // Points entered by cashier
  loyaltyPaymentAmount: number = 0; // Calculated amount (loyaltyPointsInput * redeem_amount)
  isLoyaltyEnabled: boolean = false;

  constructor(
    private readonly productService: ProductDbService,
    private readonly clientSettingsService: ClientSettingsService,
    private readonly messageService: MessageService,
  ) {}

  loadCustomerLoyaltyPoints(customerId: number, isExchangeOrder: boolean): void {
    if (!this.clientSettingsService.isLoyaltyPointsEnabled()) {
      this.resetLoyaltyData();
      return;
    }

    if (isExchangeOrder) {
      this.resetLoyaltyData();
      return;
    }

    this.productService.getCustomerLoyaltyPoints(customerId).subscribe({
      next: (response) => {
        if (response.status === 201 && response.data) {
          console.log('customerLoyalty Data', response.data)
          this.customerLoyaltyData = response.data;
          this.loyaltyPoints = response.data.rewardpoints || 0;

          const redeemAmount =
            response.data.loyality_membership?.redeem_amount || 0;
          this.loyaltyAmount = this.loyaltyPoints * redeemAmount;
          this.isLoyaltyEnabled = true;

          console.log('Loyalty loaded:', {
            points: this.loyaltyPoints,
            redeemAmount: redeemAmount,
            totalAmount: this.loyaltyAmount,
          });
        } else {
          this.resetLoyaltyData();
        }
      },
      error: (error) => {
        this.resetLoyaltyData();
      },
    });
  }

  resetLoyaltyData(): void {
    this.customerLoyaltyData = null;
    this.loyaltyPoints = 0;
    this.loyaltyAmount = 0;
    this.loyaltyPointsInput = 0;
    this.loyaltyPaymentAmount = 0;
    this.isLoyaltyEnabled = false;
  }

  hasLoyaltyPoints(): boolean {
    return !!(
      this.isLoyaltyEnabled &&
      this.customerLoyaltyData &&
      this.loyaltyPoints > 0 &&
      this.loyaltyAmount > 0
    );
  }

  isLoyaltyExpired(): boolean {
    const expiryDate = this.customerLoyaltyData?.loyality_membership?.expiry_date;
    if (!expiryDate) {
      return false; // No expiry date means never expires
    }
    return getLoyaltyExpiryDays(expiryDate) < 0;
  }

  getLoyaltyExpiryDays(): number {
    const expiryDate = this.customerLoyaltyData?.loyality_membership?.expiry_date;
    return getLoyaltyExpiryDays(expiryDate);
  }

  getLoyaltyExpiryText(): string {
    const expiryDate = this.customerLoyaltyData?.loyality_membership?.expiry_date;
    return getLoyaltyExpiryText(expiryDate);
  }

  getLoyaltyExpiryClass(): string {
    const expiryDate = this.customerLoyaltyData?.loyality_membership?.expiry_date;
    return getLoyaltyExpiryClass(expiryDate);
  }

  getMaxRedeemablePoints(): number {
    const maxRedeemPoints =
      this.customerLoyaltyData?.loyality_membership?.max_redeem_points || 0;
    if (maxRedeemPoints > 0) {
      return Math.min(this.loyaltyPoints, maxRedeemPoints); // this i did it intentional
    }
    return this.loyaltyPoints;
  }

  getMaxRedeemableAmount(): number {
    const redeemAmount =
      this.customerLoyaltyData?.loyality_membership?.redeem_amount || 0;
    return this.getMaxRedeemablePoints() * redeemAmount;
  }

  /** Returns true if the caller should recompute balance (mirrors the
   * original's unconditional trailing calculateBalance() call). */
  onLoyaltyPointsInput(orderTotal: number): void {
    if (!this.customerLoyaltyData) {
      return;
    }

    // Check if loyalty points are expired
    if (this.isLoyaltyExpired()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Loyalty Points Expired',
        detail: 'Your loyalty points have expired and cannot be redeemed',
        life: 3000,
      });
      this.loyaltyPointsInput = 0;
      this.loyaltyPaymentAmount = 0;
      return;
    }

    const redeemAmount =
      this.customerLoyaltyData.loyality_membership?.redeem_amount || 0;

    if (this.loyaltyPointsInput > this.loyaltyPoints) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Points',
        detail: `Points cannot exceed available points (${this.loyaltyPoints})`,
        life: 3000,
      });
      this.loyaltyPointsInput = this.loyaltyPoints;
    }

    this.loyaltyPaymentAmount = this.loyaltyPointsInput * redeemAmount;

    this.validateLoyaltyAmount(orderTotal);
  }

  validateLoyaltyAmount(orderTotal: number): void {
    if (!this.customerLoyaltyData) {
      return;
    }

    const minOrderValue =
      this.customerLoyaltyData.loyality_membership?.min_redeem_order_value || 0;
    const maxRedeemPoints =
      this.customerLoyaltyData.loyality_membership?.max_redeem_points || 0;
    const redeemAmount =
      this.customerLoyaltyData.loyality_membership?.redeem_amount || 0;
    const maxRedeemAmount =
      maxRedeemPoints > 0 ? maxRedeemPoints * redeemAmount : 0;

    if (orderTotal < minOrderValue) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Order Value Too Low',
        detail: `Minimum order value to use loyalty points is ₹${minOrderValue}`,
        life: 3000,
      });
      this.loyaltyPointsInput = 0;
      this.loyaltyPaymentAmount = 0;
      return;
    }

    // Check if loyalty points exceed max redeemable points per transaction
    if (maxRedeemPoints > 0 && this.loyaltyPointsInput > maxRedeemPoints) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Points Limit Exceeded',
        detail: `Maximum ${maxRedeemPoints} points (₹${maxRedeemAmount}) can be redeemed per transaction`,
        life: 3000,
      });
      this.loyaltyPointsInput = maxRedeemPoints;
      this.loyaltyPaymentAmount = maxRedeemAmount;
      return;
    }

    // Check if loyalty amount exceeds available loyalty
    if (this.loyaltyPaymentAmount > this.loyaltyAmount) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Amount',
        detail: `Loyalty amount cannot exceed available loyalty (₹${this.loyaltyAmount})`,
        life: 3000,
      });
      this.loyaltyPointsInput = this.loyaltyPoints;
      this.loyaltyPaymentAmount = this.loyaltyAmount;
      return;
    }

    // Check if loyalty amount exceeds order total
    if (this.loyaltyPaymentAmount > orderTotal) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Amount Too High',
        detail: `Loyalty amount cannot exceed order total (₹${orderTotal})`,
        life: 3000,
      });
      const maxPoints = Math.floor(orderTotal / redeemAmount);
      this.loyaltyPointsInput = maxPoints;
      this.loyaltyPaymentAmount = maxPoints * redeemAmount;
      return;
    }
  }

  // Check if loyalty can be used for current order
  canUseLoyalty(isExchangeOrder: boolean, orderTotal: number): boolean {
    if (!this.hasLoyaltyPoints() || isExchangeOrder) {
      // Clear loyalty values when loyalty cannot be used
      if (this.loyaltyPointsInput > 0) {
        this.loyaltyPointsInput = 0;
        this.loyaltyPaymentAmount = 0;
      }
      return false;
    }

    // Check if loyalty points are expired
    if (this.isLoyaltyExpired()) {
      // Clear loyalty values when expired
      if (this.loyaltyPointsInput > 0) {
        this.loyaltyPointsInput = 0;
        this.loyaltyPaymentAmount = 0;
      }
      return false;
    }

    const minOrderValue =
      this.customerLoyaltyData?.loyality_membership?.min_redeem_order_value ||
      0;

    if (orderTotal < minOrderValue) {
      // Clear loyalty values when order value is below minimum
      if (this.loyaltyPointsInput > 0) {
        this.loyaltyPointsInput = 0;
        this.loyaltyPaymentAmount = 0;
      }
      return false;
    }

    return true;
  }

  // Check and clear loyalty points if order total falls below minimum
  checkAndClearLoyaltyIfInvalid(orderTotal: number): boolean {
    if (this.loyaltyPointsInput <= 0 || !this.customerLoyaltyData) {
      return false;
    }

    const minOrderValue =
      this.customerLoyaltyData.loyality_membership?.min_redeem_order_value || 0;

    if (orderTotal < minOrderValue) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Loyalty Points Cleared',
        detail: `Order value (₹${orderTotal}) is below minimum (₹${minOrderValue}) for loyalty redemption`,
        life: 3000,
      });
      this.loyaltyPointsInput = 0;
      this.loyaltyPaymentAmount = 0;
      return true;
    }
    return false;
  }

  // Get loyalty validation message
  getLoyaltyValidationMessage(isExchangeOrder: boolean, orderTotal: number): string | null {
    if (!this.hasLoyaltyPoints() || isExchangeOrder) {
      return null;
    }

    const minOrderValue =
      this.customerLoyaltyData?.loyality_membership?.min_redeem_order_value ||
      0;

    if (orderTotal < minOrderValue) {
      return `Minimum order value for loyalty redemption is ₹${minOrderValue}`;
    }

    return null;
  }
}
